import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { styled } from '@abyss/web/tools/styled';
import { Box } from '@abyss/web/ui/Box';
import { Drawer } from '@abyss/web/ui/Drawer';
import { Flex } from '@abyss/web/ui/Flex';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import React from 'react';
import { useShallow } from 'zustand/react/shallow';

import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../hooks/useLagoon';
import { useAnalyticsStore } from '../../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../../store/useAnalyticsStore/analyticsStore';
import { useChipStore } from '../../../store/useChipStore';
import { ChipState } from '../../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../../store/useDetailsStore';
import { DetailsStore } from '../../../store/useDetailsStore/detailsStore';
import { useSearchStore } from '../../../store/useSearchStore';
import { SearchState } from '../../../store/useSearchStore/searchStore';
import { useTypeaheadStore } from '../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../store/useTypeaheadStore/typeaheadStore';
import { Constants, RecentSearchesSearchTypes } from '../../Constants';
import {
  phoneOnly,
  recentActivityDrawerDesktop,
  recentActivityDrawerMobile,
} from '../../ConstantsStyles';
import { formatDate } from '../../Utils/datesUtils/formatDate';
import { handleRecentSearchesChipClick } from '../helper';

// TODO All mocked data will be replaced with lagoon
enum SearchTypes {
  Viewed = 'Viewed',
  Visited = 'Visited',
  Searched = 'Searched',
}

const ListContainer = styled('ul', {
  margin: '15px 0 auto auto',
  listStyleType: 'none',
  counterReset: 'steps',
});

const ItemContainer = styled('li', {
  padding: '0 0 16px 20px',
  position: 'relative',
  margin: '0',

  '&:after': {
    position: 'absolute',
    top: '3px',
    left: 0,
    content: '',
    border: '1px solid $gray6',
    borderRadius: '50%',
    display: 'inline-block',
    height: '12px',
    width: '12px',
    textAlign: 'center',
    lineHeight: '24px',
    background: '#fff',
  },
  '&:before': {
    position: 'absolute',
    left: '5px',
    top: '3px',
    content: '',
    height: '100%',
    width: 0,
    borderLeft: '1px dashed #B3B3B3',
  },
  '&:last-of-type:before': {
    border: 'none',
  },
});

interface RecentSearch {
  category: string;
  pesSearchTerm: string;
  psxSearchTerm: string;
  searchType: string;
  createdDate: string;
}

type Props = {
  recentSearchesResponse: RecentSearch[];
  isOpen: boolean;
  setIsOpen: Function;
  drawerHeading: string;
};
const sources: { [key: string]: string } = {
  [RecentSearchesSearchTypes.SPECIALITY]: SearchTypes.Searched,
  [RecentSearchesSearchTypes.NAME]: SearchTypes.Searched,
  [RecentSearchesSearchTypes.ORGANIZATION_DETAIL]: SearchTypes.Viewed,
  [RecentSearchesSearchTypes.MEDICAL_GROUP_DETAIL]: SearchTypes.Viewed,
  [RecentSearchesSearchTypes.PRACTITIONER_DETAIL]: SearchTypes.Viewed,
};

export const RecentActivityDrawer = ({
  recentSearchesResponse,
  isOpen,
  setIsOpen,
  drawerHeading,
}: Props) => {
  const { navigate } = useRouter();
  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { setCoverageType, setChipValue, setOpenRecentActivity } = chipStore;

  const { setAnalyticsState } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      setAnalyticsState: state.setAnalyticsState,
    }))
  );
  const { setDetailsStore } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      setDetailsStore: state.setDetailsStore,
    }))
  );

  const { setSearchStore } = useSearchStore(
    useShallow((state: SearchState) => ({
      setSearchStore: state.setSearchStore,
    }))
  );
  const mobileScreen = useMediaQuery(phoneOnly);

  const specialityPreferenceTable = useLagoon(
    Constants.LAGOON_TABLE.SPECIALITY_PREFERENCE
  )().map((item) => ({
    rollupCode: item.rollupCode,
    specialityCode: item.specialityCode,
  }));

  const [enableRetriveTypeAheadDataFlag, enableSearchResultsV2] =
    useFeatureFlag([
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_RETRIVE_TYPEAHEAD_DATA,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RESULTS_V2,
    ]);

  const enableRetriveTypeAheadData =
    enableRetriveTypeAheadDataFlag && enableSearchResultsV2;

  const handleOnClose = () => {
    setIsOpen(false);
    setOpenRecentActivity(false);
  };

  return (
    <Drawer
      css={
        mobileScreen ? recentActivityDrawerMobile : recentActivityDrawerDesktop
      }
      data-auto-testid="heading-recent-activity"
      data-testid="heading-recent-activity"
      isOpen={isOpen}
      onClose={handleOnClose}
      position={mobileScreen ? 'bottom' : 'right'}
      size={mobileScreen ? window.innerHeight - 104 : 408}
      title= {drawerHeading}
    >
      <Box color="$white">
        <React.Fragment>
          <ListContainer>
            {recentSearchesResponse.slice(0, 10).map((item) => {
              const formatedDate = formatDate(new Date(item.createdDate));
              const result = sources[item.searchType] || SearchTypes.Viewed;
              return (
                <ItemContainer key={`recent-activity-item-${item.createdDate}`}>
                  <Flex alignContent="flex-start" direction="row">
                    <Text
                      color="$gray8"
                      css={{
                        'abyss-text-root': {
                          fontSize: '14.22px',
                          fontWeight: '$medium',
                          fontStyle: 'normal',
                          lineHeight: '20px',
                        },
                      }}
                    >
                      {result}
                    </Text>
                    <Link
                      data-auto-testid="recent-activity-keyword"
                      data-testid="recent-activity-keyword"
                      isStandardAnchor
                      onClick={() => {
                        handleRecentSearchesChipClick(
                          item,
                          navigate,
                          '',
                          {
                            chipStore,
                            setTypeaheadSearchStore,
                            setAnalyticsState,
                            setChipValue,
                            setCoverageType,
                            setDetailsStore,
                            setSearchStore,
                          },
                          specialityPreferenceTable,
                          enableRetriveTypeAheadData
                        );
                      }}
                      size="sm"
                    >
                      <Text
                        color="$interactive1"
                        css={{
                          'abyss-text-root': {
                            fontSize: '14.22px',
                            fontWeight: '$bold',
                            fontStyle: 'normal',
                          },
                        }}
                      >
                        &nbsp;"
                        {item.psxSearchTerm}"
                      </Text>
                    </Link>
                  </Flex>
                  <Text
                    color="$gray7"
                    css={{
                      'abyss-text-root': {
                        textOverflow: 'ellipsis',
                        fontStyle: 'normal',
                        lineHeight: '16px',
                        fontWeight: '$medium',
                        fontSize: '12.64px',
                      },
                    }}
                  >
                    {`${formatedDate.monthName} ${formatedDate.day}`}
                  </Text>
                </ItemContainer>
              );
            })}
          </ListContainer>
        </React.Fragment>
      </Box>
    </Drawer>
  );
};
